/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useEffect,
  useRef,
  Fragment,
  useCallback,
  useMemo,
} from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import cloneDeep from "lodash/cloneDeep";
import {
  RefreshCw,
  Plus,
  SquarePen,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import {
  PageHeader,
  Button,
  Input,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SpinnerLoader,
  Toastify,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DataTable,
  Switch,
  MultiSelectCheckboxTag,
  Label,
} from "lift-ui";
import type { TableColumn } from "lift-ui";

import { useSelector } from "react-redux";
import TableSkeleton from "../../shared/components/skeleton-loader/table";
import { useThrottledFn } from "@lift-admin/utils/throttle";
import { debounce } from "@lift-admin/utils/debounce";
import apiClient from "@lift-admin/utils/apiClient";
import {
  setEditData,
  setEditDialogOpen,
} from "../../shared/redux/action/performance.action";
import AddPerformanceForm from "../performance-accounts/add_accounts";
import type { AddPerformanceFormRef } from "../performance-accounts/add_accounts";
import { RootState } from "../../shared/redux/types";
import { useAppDispatch, useAppSelector } from "../../shared/redux/hooks";
import EditPerformance from "../performance-accounts/edit_account";
import { actionLog } from "../../App";
import {
  buildSummary,
  diffList,
  humanizeValue,
} from "@lift-admin/utils/logChangeFormatter";
import { LIST_OF_PERFORMANCE_DATA } from "../performance-accounts/constants";
import { useAuthContext } from "@lift-admin/utils/provider";

// Add this interface near the top of the file with other interfaces/types
interface Brand {
  brand_id: string;
  brand_name: string;
  ad_accounts: Array<{
    id: number;
    backfill_status: string;
    [key: string]: any;
  }>;
}

// Add type for column selection
interface ColumnConfig {
  align: "left" | "center" | "right";
  sx: {
    color: string;
    minWidth: string;
    maxWidth: string;
    [key: string]: any;
  };
}

// Add type for row props
interface RowProps {
  row: {
    brand_id?: string;
    brand_name: string;
    name?: string;
    ad_accounts?: any[];
  };
  isExpanded: boolean;
  onExpandToggle: () => void;
  style?: React.CSSProperties;
  selectedColumns: string[];
  renderAllColumns: () => { name: string; align: string; sx: any }[];
  renderCellContent: (item: any, columnName: string, brandRow?: any) => React.ReactNode;
  collapsedTableRefs: any;
}

const PerfAccounts = () => {
  const { canEdit, canManage } = useAuthContext();
  const hasEditAccess = canEdit("media_acct_mgmt");
  const hasManageAccess = canManage("media_acct_mgmt");

  const dispatch = useAppDispatch();
  const { performanceData, editDialogOpen } = useAppSelector(
    (state) => state.performance
  );
  //add Performance items
  const [singlePlatData, setSinglePlatData] = useState<any>([]);
  const [addPerfDialogOpen, setAddPerfDialogOpen] = React.useState(false);
  const [editValues, setEditValues] = useState<any>(null);
  const [editPerfValues, setEditPerfValues] = useState<any>(null);

  const [changeItem, setChangeItem] = useState<any>({
    id: null,
    name: null,
    account_name: "",
    account_id: "",
    attribution_window: "",
    is_mbm: "No",
  });
  const [childItem, setChildItem] = useState<any>({
    id: null,
    name: "",
    account_name: "",
    account_id: "",
    attribution_window: "",
    is_mbm: "No",
  });
  const [dataSources, setDataSources] = useState([]);

  // / /Update Active Status of Media platform accounts
  const [aboutToUpdateActivePlatformShow, setAboutToUpdateActivePlatformShow] =
    useState<any>(false);
  const [
    aboutToUpdateActivePlatformProgress,
    setAboutToUpdateActivePlatformProgress,
  ] = useState<any>(false);
  const [aboutToUpdateActivePlatform, setAboutToUpdateActivePlatform] =
    useState<any>(null);

  const [dialogMBM, setDialogMBM] = React.useState(false);
  const [isAgreeClickedMBM, setIsAgreeClickedMBM] = React.useState(false);
  const [pendingChangeEditAdd, setPendingChangeEditAdd] = useState(null);
  const [editSinglePlatData, setEditSinglePlatData] = useState<any>(null);
  const [editChangeItemNew, setEditChangeItemNew] = useState<any>(null);
  const [perfValues, setPerfValues] = useState<any>([]);

  const [perfValuesBackup, setPerfValuesBackup] = useState<any>([]);
  const [page, setPage] = useState<any>(0);
  const [rowsPerPage, setRowsPerPage] = useState<any>(25);
  const [showAddPerfSheet, setShowAddPerfSheet] = useState(false);
  const addPerfFormRef = useRef<AddPerformanceFormRef>(null);
  const [initialBrandForAdd, setInitialBrandForAdd] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem("addPerfForNewBrand");
    if (stored) {
      try {
        const brand = JSON.parse(stored);
        setInitialBrandForAdd(brand);
        setShowAddPerfSheet(true);
      } catch { /* ignore */ }
      sessionStorage.removeItem("addPerfForNewBrand");
    }
  }, []);

  const [pendingChange, setPendingChange] = useState(null);
  const [editPendingChange, setEditPendingChange] = useState(null);
  const [platName, setPlatName] = useState<any>("");
  const [perfData, setPerfData] = useState<any>([]);
  const [values, setValues] = useState([0, 0, 0]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMetaplierAccess, setHasMetaplierAccess] = useState<any>({
    hasAccess: false,
    id: null,
  });
  const [editPerfDialogOpen, setEditPerfDialogOpen] = React.useState(false);

  const [searchText, setSearchText] = useState<any>("");
  const [searchedBrands, setSearchedBrands] = useState<any>("");
  const [searchQuery, setSearchQuery] = useState<any>("");

  const [brands, setBrands] = useState<any>([]);

  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(
    new Set()
  );
  const [editSinglePlatDataAddPerf, setEditSinglePlatDataAddPerf] =
    useState<any>(null);

  // Define state for source_of_truth toggle
  const [aboutToUpdateSourceOfTruthShow, setAboutToUpdateSourceOfTruthShow] =
    useState<any>(false);
  const [
    aboutToUpdateSourceOfTruthProgress,
    setAboutToUpdateSourceOfTruthProgress,
  ] = useState<any>(false);
  const [aboutToUpdateSourceOfTruth, setAboutToUpdateSourceOfTruth] =
    useState<any>(null);

  // Add new states for backfill status update
  const [aboutToUpdateBackfillStatus, setAboutToUpdateBackfillStatus] =
    useState<any>(null);
  const [backfillAnchorEl, setBackfillAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [backfillProgress, setBackfillProgress] = useState(false);

  const tableContainerRef: any = useRef(null);
  const collapsedTableRefs: any = useRef({});

  // 2. Add these new state variables to store scroll positions
  const [tableScrollPosition, setTableScrollPosition] = useState({
    left: 0,
    top: 0,
  });
  const [nestedTableScrollPositions, setNestedTableScrollPositions] = useState(
    {}
  );

  // Add this near other state declarations
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  // 3. Add this function to save scroll position before state changes
  const saveScrollPositions = useCallback(() => {
    // Save main table scroll position
    if (tableContainerRef.current) {
      setTableScrollPosition({
        left: tableContainerRef.current.scrollLeft,
        top: tableContainerRef.current.scrollTop,
      });
    }

    // Save nested tables scroll positions
    const newPositions: any = {};
    Object.entries(collapsedTableRefs.current).forEach(
      ([key, ref]: [any, any]) => {
        if (ref && ref.querySelector("div")) {
          const element = ref.querySelector("div");
          newPositions[key] = {
            left: element.scrollLeft,
            top: element.scrollTop,
          };
        }
      }
    );
    setNestedTableScrollPositions(newPositions);
  }, []);

  // 4. Add this effect to restore scroll position after render
  useEffect(() => {
    // Restore main table scroll position
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = tableScrollPosition.left;
      tableContainerRef.current.scrollTop = tableScrollPosition.top;
    }

    // Restore nested tables scroll positions
    Object.entries(nestedTableScrollPositions).forEach(
      ([key, position]: [any, any]) => {
        const ref = collapsedTableRefs.current[key];
        if (ref && ref.querySelector("div")) {
          const element = ref.querySelector("div");
          element.scrollLeft = position.left;
          element.scrollTop = position.top;
        }
      }
    );
  }, [tableScrollPosition, nestedTableScrollPositions]);

  // Add backfill status options
  const backfillStatusOptions = [
    "No Access",
    "Pending",
    "Running",
    "Completed",
    "Error",
  ];

  // Add color mapping for backfill status chips
  const getBackfillStatusColor = (status: string) => {
    switch (status) {
      case "No Access":
        return "default";
      case "Pending":
        return "warning";
      case "Running":
        return "info";
      case "Completed":
        return "success";
      case "Error":
        return "error";
      default:
        return "default";
    }
  };

  const { admin_auth, superAdmin } = useSelector((state: any) => state.admin);
  const user = JSON.parse(localStorage.getItem("user") || "");
  const handleCloseAddPerfDialogCloseBtn = () => {
    setAddPerfDialogOpen(false);
  };

  const handlePendingChangeEdit = (e: any) => {
    let new_item: any = "";

    new_item = { ...editChangeItemNew };

    let req_obj: any = "";

    if (perfData.length > 0) {
      req_obj = perfData.filter((item: any) => {
        return item.id === hasMetaplierAccess?.id;
      })[0];

      if (req_obj) {
        req_obj.is_mbm = "No";

        const new_arr = [...perfData];
        new_arr.splice(req_obj.id - 1, 1, req_obj);
        setPerfData(new_arr);
        new_item[e.target.name] = e.target.value;

        setEditChangeItemNew(new_item);
      }
    }

    setHasMetaplierAccess({ hasAccess: true, id: new_item.id });
    setIsAgreeClickedMBM(false);
    return;
  };

  const handlePendingChange = (e: any) => {
    let new_item: any = "";

    new_item = { ...changeItem };

    let req_obj: any = "";

    if (perfData.length > 0) {
      req_obj = perfData.filter((item: any) => {
        return item.id === hasMetaplierAccess?.id;
      })[0];

      if (req_obj) {
        req_obj.is_mbm = "No";

        const new_arr = [...perfData];
        new_arr.splice(req_obj.id - 1, 1, req_obj);
        setPerfData(new_arr);
        new_item[e.target.name] = e.target.value;

        setChangeItem(new_item);
      }
    }

    if (singlePlatData.length > 0) {
      req_obj = singlePlatData.filter((item: any) => {
        return item.id === hasMetaplierAccess?.id;
      })[0];
      if (req_obj) {
        req_obj.is_mbm = "No";
        const new_arr = [...singlePlatData];
        new_arr.splice(req_obj.id - 1 - perfData.length, 1, req_obj);
        setSinglePlatData(new_arr);
        new_item[e.target.name] = e.target.value;

        setChangeItem(new_item);
      }
    }
    setHasMetaplierAccess({ hasAccess: true, id: new_item.id });
    setIsAgreeClickedMBM(false);
    return;
  };

  useEffect(() => {
    if (isAgreeClickedMBM && pendingChange) {
      handlePendingChange(pendingChange);
    }
  }, [isAgreeClickedMBM]);

  useEffect(() => {
    if (isAgreeClickedMBM && editPendingChange) {
      handlePendingChangeEdit(editPendingChange);
    }
  }, [isAgreeClickedMBM]);

  const handleCloseEditPerfDialog = () => {
    setEditPerfDialogOpen(false);
  };

  const handleGetDataSources = () => {
    const displayNames: any = LIST_OF_PERFORMANCE_DATA.map(
      (item) => item.display_name
    );
    setDataSources(displayNames);
  };
  useEffect(() => {
    handleGetDataSources();
  }, []);

  const handleAddPlatName = (e: any) => {
    if (changeItem.name) {
      Toastify("error", "Add below one first then you can add new one");
    } else {
      if (platName !== "") {
        const new_obj = { ...childItem };
        new_obj["name"] = platName;
        new_obj["id"] =
          singlePlatData.length > 0
            ? perfData.length + 1 + singlePlatData.length
            : perfData.length + 1;

        setChangeItem({ ...changeItem, name: platName, id: new_obj.id });
        const new_obj_plat = [...singlePlatData];

        new_obj_plat.push(new_obj);

        setSinglePlatData(new_obj_plat);
        setChildItem({
          id: null,
          name: "",
          account_name: "",
          account_id: "",
          attribution_window: "",
          is_mbm: "No",
        });
        setPlatName("");
      } else {
        Toastify("error", "Data Source Name is required");
      }
    }
  };



  const handleChangeChildItem = (e: any) => {
    const new_obj = { ...changeItem };

    if (e.target.name === "name") {
      new_obj[e.target.name] = e.target.value;
    } else {
      if (e.target.name === "is_mbm") {
        if (e.target.value === "Yes") {
          if (hasMetaplierAccess.hasAccess) {
            setDialogMBM(true);
            setPendingChange(e);
          } else {
            new_obj[e.target.name] = e.target.value;
            setHasMetaplierAccess({ hasAccess: true, id: new_obj.id });
          }
        } else {
          if (hasMetaplierAccess?.id === new_obj.id) {
            setHasMetaplierAccess({ hasAccess: false, id: null });
          }
          new_obj[e.target.name] = e.target.value;
        }
      } else {
        new_obj[e.target.id] = e.target.value;
      }
    }
    setChangeItem(new_obj);
  };

  const handleEditChildItem = (data: any, e: any) => {
    if (e.target.textContent.toLowerCase() === "edit") {
      setEditChangeItemNew(cloneDeep(data));
    } else {
      if (
        editChangeItemNew.account_id !== "" &&
        editChangeItemNew.account_name !== ""
      ) {
        let new_obj_oo = { ...editChangeItemNew };
        const attr_values = `${editPerfValues[0]}/${editPerfValues[1]}/${editPerfValues[2]}`;

        if (
          editChangeItemNew.name === "Pinterest Ads" &&
          attr_values === "0/0/0"
        ) {
          Toastify("error", "Attribution values cannot be 0/0/0 for Pinterest");
        } else {
          if (editChangeItemNew.name !== "Facebook Ads") {
            new_obj_oo = {
              ...new_obj_oo,
              attribution_window: attr_values,
              is_mbm: "No",
            };
          } else {
            new_obj_oo = { ...new_obj_oo, attribution_window: attr_values };
          }
          setEditSinglePlatData(new_obj_oo);

          setEditChangeItemNew(null);
        }
      } else {
        Toastify("error", "Account Name and Account Id cannot be empty");
      }
    }
  };


  const handleClickEditPerfDialogOpen = () => {
    setEditPerfDialogOpen(true);
  };

  //edit performance dialog
  const handleEditPlatform = async (item: any, brandRow?: any) => {
    const user = JSON.parse(localStorage.getItem("user") || "");
    // Include brand information from parent row if available
    const editDataWithBrand = brandRow ? {
      ...item,
      brand_id: brandRow.brand_id,
      brand_name: brandRow.brand_name,
    } : item;
    dispatch(setEditData(editDataWithBrand));
    dispatch(setEditDialogOpen(true));

    // Log edit dialog opened
    const desc = {
      message: `Edit opened for performance account "${item?.account_name || "N/A"}"`,
      category: "performance",
      action: "edit_dialog_opened",
      user_id: user.id,
      user_name: user.name,
      account_id: item?.account_id,
      account_name: item?.account_name,
      brand_id: editDataWithBrand?.brand_id || null,
      brand_name: editDataWithBrand?.brand_name || null,
    };
    await actionLog?.actionLogTrack(JSON.stringify(desc), false);
  };

  const handleAddChildItemToPlat = (item: any, index: any, e: any) => {
    if (e.target.textContent.toLowerCase() === "edit") {
      if (!changeItem.id) {
        setEditSinglePlatDataAddPerf(item);
        const attribution_window = item.attribution_window.split("/");
        setEditValues(attribution_window);
      } else {
        Toastify("error", "First Complete adding new platform");
      }
    } else if (e.target.textContent.toLowerCase() === "add") {
      if (
        changeItem.account_name !== "" &&
        changeItem.account_id !== "" &&
        changeItem.name !== ""
      ) {
        const attr_window = `${values[0]}/${values[1]}/${values[2]}`;
        if (changeItem.name === "Pinterest Ads" && attr_window === "0/0/0") {
          Toastify("error", "Attribution window cannot be 0/0/0 in Pinterest");
        } else {
          const new_obj = [...singlePlatData];
          const desired_obj = new_obj[index];
          desired_obj["name"] = changeItem.name;
          desired_obj["account_name"] = changeItem.account_name;
          desired_obj["account_id"] = changeItem.account_id;
          desired_obj["attribution_window"] = attr_window;
          setValues([0, 0, 0]);

          if (changeItem.name !== "Facebook Ads") {
            desired_obj["is_mbm"] = "No";
          } else {
            desired_obj["is_mbm"] = changeItem.is_mbm;
          }

          new_obj.splice(index, 1, desired_obj);

          setSinglePlatData(new_obj);
          setChangeItem({
            name: null,
            account_name: "",
            account_id: "",
            id: null,
            is_mbm: "No",
            attribution_window: "",
          });
        }
      } else {
        Toastify("error", "Account Id, Account Name are required");
      }
    } else {
      let new_obj = { ...editSinglePlatDataAddPerf };
      const attr_window = `${editValues[0]}/${editValues[1]}/${editValues[2]}`;
      new_obj = { ...new_obj, attribution_window: attr_window };
      const singlePlatObj = [...singlePlatData];
      singlePlatObj.splice(new_obj.id - 1 - perfData.length, 1, new_obj);
      setSinglePlatData(singlePlatObj);

      setEditSinglePlatDataAddPerf(null);
      setEditValues(null);
    }
  };

  const handleChangeEditSinglePlatDataAddPerf = (e: any) => {
    const new_obj = { ...editSinglePlatDataAddPerf };
    if (e.target.name === "name") {
      new_obj[e.target.name] = e.target.value;
    } else {
      if (e.target.name === "is_mbm") {
        if (e.target.value === "Yes") {
          if (hasMetaplierAccess.hasAccess) {
            setDialogMBM(true);
            setPendingChangeEditAdd(e);
          } else {
            setHasMetaplierAccess({ hasAccess: true, id: new_obj.id });
            new_obj[e.target.name] = e.target.value;
          }
        } else {
          if (hasMetaplierAccess.id === new_obj.id) {
            setHasMetaplierAccess({ hasAccess: false, id: null });
          }
          new_obj[e.target.name] = e.target.value;
        }
      } else {
        new_obj[e.target.id] = e.target.value;
      }
    }
    setEditSinglePlatDataAddPerf(new_obj);
  };

  const handleKeyDown = (index: any, e: any) => {
    const newValues = [...values];
    if (e.key === "ArrowUp") {
      newValues[index]++;
      setValues(newValues);
    } else if (e.key === "ArrowDown") {
      newValues[index] = Math.max(0, newValues[index] - 1);
      setValues(newValues);
    }
  };

  // Update data on page change
  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
  };

  // Update data when rows per page count is changed
  const handleChangeRowsPerPage = async (value: string) => {
    const newRowsPerPage = Number(value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const get_ref = useRef(true);
  useEffect(() => {
    if (get_ref.current) {
      get_ref.current = false;
      getPerfValues();
    }
  }, []);

  //super token

  const [loadList, setLoadList] = useState(true);

  const getPerfValues = () => {
    if (superAdmin) {
      apiClient
        .get("/brand/v1/paid_ad_accounts/")
        .then((res) => {
          setLoadList(false);
          if (!searchQuery.trim()) {
            setPerfValues(
              res.data.response.sort(customSortBasedOnDate).reverse()
            );
            setPerfValuesBackup(
              res.data.response.sort(customSortBasedOnDate).reverse()
            );
          } else {
            const filteredBrands = res.data.response.filter((brand: any) =>
              searchedBrands.includes(brand.brand_id)
            );

            setPerfValues(filteredBrands.sort(customSortBasedOnDate).reverse());
          }
        })
        .catch((err) => {
          Toastify("error", err);
          console.error(err);
        });
    }
  };

  //Getting all brands
  const getBrands = async () => {
    try {
      const res = await apiClient.get("/brand/v1/admin/brands/all");

      setBrands(res.data.response);
    } catch (error: any) {
      Toastify("error", error);
    }
  };

  useEffect(() => {
    getBrands();
  }, []);

  const throttleGetBrands = useThrottledFn(getPerfValues, 1000);

  const customSortBasedOnDate = (a: any, b: any) => {
    if (superAdmin) {
      return new Date(a.brand_id).getTime() - new Date(b.brand_id).getTime();
    } else {
      return new Date(a.brand_id).getTime() - new Date(b.brand_id).getTime();
    }
  };

  // Update function
  const updateActivePlatformStatus = async () => {
    setAboutToUpdateActivePlatformProgress(true);

    // Save current scroll positions
    saveScrollPositions();

    const user = JSON.parse(localStorage.getItem("user") || "");

    let formData = {
      is_active: aboutToUpdateActivePlatform.is_active ? false : true,
      last_modified_column: ["is_active"],
      modified_by: user.name,
    };

    try {
      const res = await apiClient.put(
        `/brand/v1/paid_ad_accounts/update/${aboutToUpdateActivePlatform.id}`,
        formData
      );

      if (res.data.message) {
        // Update both states immediately
        const updateData = (dataArray: Brand[]) => {
          return dataArray.map((brand) => {
            // Update ad_accounts array within each brand
            const updatedAdAccounts = brand.ad_accounts.map((account) =>
              account.id === aboutToUpdateActivePlatform.id
                ? { ...account, is_active: formData.is_active }
                : account
            );

            // Return brand with updated ad_accounts
            return {
              ...brand,
              ad_accounts: updatedAdAccounts,
            };
          });
        };

        setPerfValues((currentValues: any) => updateData(currentValues));
        setPerfValuesBackup((currentBackup: any) => updateData(currentBackup));

        // Log the action
        const desc = {
          message: buildSummary(
            "Performance account",
            aboutToUpdateActivePlatform?.account_name,
            formData.is_active ? "activated" : "deactivated"
          ),
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "performance",
          action: formData.is_active
            ? "activate_paid_ad_account"
            : "deactivate_paid_ad_account",
          account_id: aboutToUpdateActivePlatform?.account_id,
          account_name: aboutToUpdateActivePlatform?.account_name,
          brand_id: aboutToUpdateActivePlatform?.brand_id || null,
          brand_name: aboutToUpdateActivePlatform?.brand_name || null,
          user_id: user.id,
          user_name: user.name,
          changes: [
            {
              label: "Status",
              from: humanizeValue(!formData.is_active, {
                type: "boolean",
                boolStyle: "activeInactive",
              }),
              to: humanizeValue(formData.is_active, {
                type: "boolean",
                boolStyle: "activeInactive",
              }),
            },
          ],
        };
        await actionLog?.actionLogTrack(JSON.stringify(desc), false);

        setAboutToUpdateActivePlatformProgress(false);
        setAboutToUpdateActivePlatformShow(false);
        setAboutToUpdateActivePlatform(null);

        Toastify("success", res.data.message);
      }
    } catch (error: any) {
      setAboutToUpdateActivePlatformProgress(false);
      setAboutToUpdateActivePlatformShow(false);
      setAboutToUpdateActivePlatform(null);
      Toastify("error", error);

      // Log error action
      const errorDesc = {
        message: `Failed to ${aboutToUpdateActivePlatform?.is_active ? "deactivate" : "activate"} performance account "${aboutToUpdateActivePlatform?.account_name || "N/A"}": ${error?.response?.data?.message || error.message || error}`,
        category: "performance",
        action: aboutToUpdateActivePlatform?.is_active
          ? "deactivate_paid_ad_account"
          : "activate_paid_ad_account",
        status: error.response?.status || "Unknown",
        method: "PUT",
        url: `/brand/v1/paid_ad_accounts/update/${aboutToUpdateActivePlatform.id}`,
        account_id: aboutToUpdateActivePlatform?.account_id,
        account_name: aboutToUpdateActivePlatform?.account_name,
        brand_id: aboutToUpdateActivePlatform?.brand_id || null,
        brand_name: aboutToUpdateActivePlatform?.brand_name || null,
        user_id: user.id,
        user_name: user.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  // Search function
  const searchResults = async (query: any) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setPerfValues(perfValuesBackup);
      setSearchedBrands([]);
      return;
    }

    const results = perfValuesBackup.filter(
      (item: any) =>
        superAdmin &&
        item.brand_name?.toLowerCase().includes(query.toLowerCase())
    );

    setSearchedBrands(results.map((item: any) => item.brand_id));
    setPerfValues(results);
  };

  // Keep the search debounced
  const searchOptimized = useCallback(debounce(searchResults, 600), [
    perfValuesBackup,
  ]);

  // Handle search input
  const handleSearch = (e: any) => {
    const query = e.target.value;
    searchOptimized(query);
  };

  // Remove getPerfValues from the update function since we're handling the update locally
  const admin_data: any = localStorage.getItem("admin_auth")
    ? JSON.parse(localStorage.getItem("admin_auth") as string)
    : null;

  const handleRemoveChildItemFromPlat = (index: any) => {
    const new_obj = [...singlePlatData];
    const desired_arr = new_obj;
    if (hasMetaplierAccess.id === index + 1) {
      setHasMetaplierAccess({ hasAccess: false, id: null });
    }
    desired_arr.splice(index, 1);
    for (let i = index; i < desired_arr.length; i++) {
      if (hasMetaplierAccess.id === desired_arr[i]["id"]) {
        setHasMetaplierAccess({ ...hasMetaplierAccess, id: i + 1 });
      }
      if (desired_arr[i]["id"] !== i + 1) {
        desired_arr[i]["id"] = i + 1;
      } else {
        continue;
      }
    }
    setSinglePlatData(desired_arr);
    setChangeItem({
      name: null,
      account_name: "",
      account_id: "",
      id: null,
      is_mbm: "No",
      attribution_window: "",
    });
  };

  const handleAddPerfSheetClose = () => {
    setShowAddPerfSheet(false);
    getPerfValues(); // Refresh data when sheet closes
  };

  const handleExpandRow = async (rowId: string) => {
    // Save scroll position before changing state
    saveScrollPositions();
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const updateSourceOfTruthStatus = async () => {
    setAboutToUpdateSourceOfTruthProgress(true);

    // Save current scroll positions
    saveScrollPositions();

    const user = JSON.parse(localStorage.getItem("user") || "");

    // Make sure we're sending integer values, not booleans
    let formData = {
      source_of_truth: aboutToUpdateSourceOfTruth.source_of_truth
        ? false
        : true,
      last_modified_column: ["source_of_truth"],
      modified_by: user.name,
    };



    try {
      const res = await apiClient.put(
        `/brand/v1/paid_ad_accounts/update/${aboutToUpdateSourceOfTruth.id}`,
        formData
      );

      if (res.data.message) {
        // Update both states immediately with the boolean value for UI
        const updateData = (dataArray: any[]) => {
          return dataArray.map((brand) => {
            // Update ad_accounts array within each brand
            const updatedAdAccounts = brand.ad_accounts.map((account: any) =>
              account.id === aboutToUpdateSourceOfTruth.id
                ? {
                  ...account,
                  source_of_truth:
                    !aboutToUpdateSourceOfTruth.source_of_truth,
                }
                : account
            );

            return {
              ...brand,
              ad_accounts: updatedAdAccounts,
            };
          });
        };

        setPerfValues((currentValues: any) => updateData(currentValues));
        setPerfValuesBackup((currentBackup: any) => updateData(currentBackup));

        // Log the action
        const desc = {
          message: `Performance account "${aboutToUpdateSourceOfTruth?.account_name}" ${formData.source_of_truth ? "set as Primary" : "removed as Primary"}`,
          status: res.status,
          method: res.config.method,
          url: res.config.url,
          category: "performance",
          action: "update_source_of_truth",
          account_id: aboutToUpdateSourceOfTruth?.account_id,
          account_name: aboutToUpdateSourceOfTruth?.account_name,
          brand_id: aboutToUpdateSourceOfTruth?.brand_id || null,
          brand_name: aboutToUpdateSourceOfTruth?.brand_name || null,
          user_id: user.id,
          user_name: user.name,
          changes: [
            {
              label: "Primary",
              from: humanizeValue(!formData.source_of_truth, {
                type: "boolean",
                boolStyle: "yesNo",
              }),
              to: humanizeValue(formData.source_of_truth, {
                type: "boolean",
                boolStyle: "yesNo",
              }),
            },
          ],
        };
        await actionLog?.actionLogTrack(JSON.stringify(desc), false);

        setAboutToUpdateSourceOfTruthProgress(false);
        setAboutToUpdateSourceOfTruthShow(false);
        setAboutToUpdateSourceOfTruth(null);

        Toastify("success", res.data.message);
      }
    } catch (error: any) {
      setAboutToUpdateSourceOfTruthProgress(false);
      setAboutToUpdateSourceOfTruthShow(false);
      setAboutToUpdateSourceOfTruth(null);

      // Log the error action
      const errorDesc = {
        message: `Failed to update Primary status for performance account "${aboutToUpdateSourceOfTruth?.account_name || "N/A"}": ${error?.response?.data?.message || error.message || error}`,
        category: "performance",
        action: "update_source_of_truth",
        status: error.response?.status || 500,
        method: "PUT",
        url: `/brand/v1/paid_ad_accounts/update/${aboutToUpdateSourceOfTruth.id}`,
        account_id: aboutToUpdateSourceOfTruth?.account_id,
        account_name: aboutToUpdateSourceOfTruth?.account_name,
        brand_id: aboutToUpdateSourceOfTruth?.brand_id || null,
        brand_name: aboutToUpdateSourceOfTruth?.brand_name || null,
        user_id: user.id,
        user_name: user.name,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);

      Toastify("error", error);
    }
  };

  // Update the getBackfillStatusStyle function to handle all original statuses
  const getBackfillStatusStyle = (status: string) => {
    switch (status) {
      case "No Access":
        return "bg-red-100 text-red-700 border-red-400";
      case "Pending":
        return "bg-yellow-100 text-yellow-700 border-yellow-400";
      case "Running":
        return "bg-blue-100 text-blue-700 border-blue-400";
      case "Completed":
        return "bg-green-100 text-green-700 border-green-400";
      case "Error":
        return "bg-red-100 text-red-700 border-red-400";
      default:
        return "bg-yellow-100 text-yellow-700 border-yellow-400";
    }
  };

  // Add state for selected columns
  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    "Account Type",
    "Account Name",
    "Account Id",
    "Property Name",
    "Property Id",
  ]);
  // Update allColumns to remove Active and Actions since they'll be fixed
  const allColumns: string[] = [
    "Account Type",
    "Account Name",
    "Account Id",
    "Property Name",
    "Property Id",
    "Selected Conversion Event",
    "Selected Conversion Tag Names",
    "Attribution Window",
    "Attribution Basis",
    "Metaplier Access",
    "Primary Conversion",
    "Secondary Conversion",
    "Tertiary Conversion",
    "Track EMQ",
    ...(import.meta.env.VITE_ENVIRONMENT === "development" ||
      import.meta.env.VITE_ENVIRONMENT === "staging"
      ? ["Backfill Status"]
      : []),
    "Primary",
    "Automation Enabled",
    "Account Config",
  ];

  // Function to get column configuration based on column name
  const getColumnConfig = (columnName: string) => {
    const baseConfig = {
      align: "left" as const,
      className: "text-left min-w-[120px] max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap",
    };

    switch (columnName) {
      case "Account Name":
      case "Selected Conversion Event":
      case "Selected Conversion Tag Names":
        return {
          ...baseConfig,
          className: "text-left min-w-[150px] max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap",
        };
      case "Actions":
        return {
          ...baseConfig,
          align: "center" as const,
          className: "text-center min-w-[100px]",
        };
      default:
        return baseConfig;
    }
  };

  // Function to render all columns including fixed ones
  const renderAllColumns = () => {
    const selectedColumnConfigs = selectedColumns.map((columnName) => ({
      name: columnName,
      ...getColumnConfig(columnName),
    }));

    // Add fixed columns
    const fixedColumns = [
      {
        name: "Active",
        ...getColumnConfig("Active"),
      },
    ];

    // Only add Actions column if user has edit access
    if (hasEditAccess) {
      fixedColumns.push({
        name: "Actions",
        ...getColumnConfig("Actions"),
      });
    }

    return [...selectedColumnConfigs, ...fixedColumns];
  };

  // Column selection change (used by SearchDropdownMultiSelect)
  const handleColumnSelectionChange = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      const user = JSON.parse(localStorage.getItem("user") || "");
      const previousColumns = selectedColumns;
      setSelectedColumns(ids);
      const columnDiff = diffList(previousColumns, ids);
      if (!columnDiff.added.length && !columnDiff.removed.length) return;
      const desc = {
        message: buildSummary(
          "Performance accounts columns",
          null,
          "updated",
          (columnDiff.added.length ? 1 : 0) + (columnDiff.removed.length ? 1 : 0)
        ),
        category: "performance",
        action: "column_selection_changed",
        user_id: user.id,
        user_name: user.name,
        changes: [
          {
            label: "Columns",
            added: columnDiff.added,
            removed: columnDiff.removed,
          },
        ],
      };
      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    },
    [selectedColumns]
  );

  // Function to render cell content based on column name
  const renderCellContent = (item: any, columnName: string, brandRow?: any) => {
    switch (columnName) {
      case "Account Type":
        return item.display_name;
      case "Account Name":
        return (
          <Tooltip title={item.account_name}>
            <span className="truncate">{item.account_name}</span>
          </Tooltip>
        );
      case "Account Id":
        return (
          <Tooltip title={item.account_id}>
            <span className="truncate">{item.account_id}</span>
          </Tooltip>
        );
      case "Property Name":
        return item.property_name || "N/A";
      case "Property Id":
        return item.property_id || "N/A";
      case "Selected Conversion Event":
        return item.selected_conversion_event || "N/A";
      case "Selected Conversion Tag Names":
        return item.selected_conversion_tag_names || "N/A";
      case "Attribution Window":
        return item?.formal_name === "Meta Ads" &&
          item?.attribution_window === "DEFAULT"
          ? "Use unified attribution"
          : item?.attribution_window || "N/A";
      case "Attribution Basis":
        return item.attribution_basis || "N/A";
      case "Metaplier Access":
        return item.is_mbm ? "Yes" : "No";
      case "Primary Conversion":
        return item?.emq_primary_conversion || "N/A";
      case "Secondary Conversion":
        return item?.emq_secondary_conversion || "N/A";
      case "Tertiary Conversion":
        return item?.emq_tertiary_conversion || "N/A";
      case "Track EMQ":
        return item.is_emq ? "Yes" : "No";
      case "Backfill Status":
        return (
          <Tooltip title={item.backfill_status || "Pending"}>
            <span className={`px-2 py-1 rounded-full text-xs border ${getBackfillStatusStyle(item.backfill_status || "Pending")}`}>
              {item.backfill_status || "Pending"}
            </span>
          </Tooltip>
        );
      case "Active":
        return hasEditAccess ? (
          <Tooltip
            title={
              item.is_active ? "Click to disactivate" : "Click to Activate"
            }
          >
            <Switch
              checked={!!item.is_active}
              onCheckedChange={() => {
                setAboutToUpdateActivePlatformShow(true);
                setAboutToUpdateActivePlatform(item);
              }}
            />
          </Tooltip>
        ) : (
          <span className={`px-2 py-1 rounded-full text-xs ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
            {item.is_active ? "Active" : "Inactive"}
          </span>
        );
      case "Primary":
        return hasEditAccess ? (
          <Tooltip
            title={
              item.source_of_truth
                ? "Click to disable Primary"
                : "Click to enable Primary"
            }
          >
            <Switch
              checked={Boolean(item.source_of_truth)}
              onCheckedChange={() => {
                setAboutToUpdateSourceOfTruthShow(true);
                setAboutToUpdateSourceOfTruth(item);
              }}
            />
          </Tooltip>
        ) : (
          <span className={`px-2 py-1 rounded-full text-xs ${item.source_of_truth ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
            {item.source_of_truth ? "Yes" : "No"}
          </span>
        );
      case "Automation Enabled":
        return item.is_automation_enabled ? "Yes" : "No";
      case "Account Config":
        return item.account_config || "N/A";
      case "Actions":
        return hasEditAccess ? (
          <Tooltip
            title={
              !item.is_active
                ? "You have to active the Platform to edit"
                : "Edit"
            }
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              disabled={!item.is_active}
              onClick={() => {
                if (item.is_active) {
                  // Use brandRow when provided (e.g. from sheet); otherwise find parent from perfValues
                  const parentBrandRow = brandRow ?? perfValues.find((b: any) =>
                    b.ad_accounts?.some((acc: any) => acc.id === item.id)
                  );
                  handleEditPlatform(item, parentBrandRow);
                }
              }}
            >
              <SquarePen className="h-4 w-4 text-black" />
            </Button>
          </Tooltip>
        ) : null;
      default:
        return "N/A";
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(perfValues.length / rowsPerPage);
  const startIndex = page * rowsPerPage + 1;
  const endIndex = Math.min((page + 1) * rowsPerPage, perfValues.length);

  // Main DataTable: paginated brands for lift-ui DataTable
  const paginatedBrands = useMemo(
    () => perfValues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [perfValues, page, rowsPerPage]
  );

  // Flattened accordion data: brand row + optional detail row (column selector + accounts table) per brand
  const flattenedTableData = useMemo(() => {
    const rows: any[] = [];
    paginatedBrands.forEach((brand: Brand) => {
      const isExpanded = expandedRows.has(brand.brand_id);
      rows.push({ __rowType: "brand", ...brand, __isExpanded: isExpanded });
      if (isExpanded) {
        rows.push({ __rowType: "detail", __parentBrand: brand });
      }
    });
    return rows;
  }, [paginatedBrands, expandedRows]);

  // Columns for the nested accounts DataTable (inside accordion)
  const accountsTableColumnsForBrand = useCallback(
    (parentBrand: Brand): TableColumn<any>[] => {
      const cols: TableColumn<any>[] = selectedColumns.map((colName) => ({
        key: colName,
        header: colName,
        accessor: () => "",
        render: (_value: any, row: any) => renderCellContent(row, colName, parentBrand),
      }));
      cols.push({
        key: "Active",
        header: "Active",
        accessor: () => "",
        align: "center",
        render: (_value: any, row: any) => renderCellContent(row, "Active", parentBrand),
      });
      if (hasEditAccess) {
        cols.push({
          key: "Actions",
          header: "Actions",
          accessor: () => null,
          align: "center",
          render: (_value: any, row: any) => renderCellContent(row, "Actions", parentBrand),
        });
      }
      return cols;
    },
    [selectedColumns, hasEditAccess]
  );

  // Main table: single column — brand row = expand + name + count; detail row = Columns + nested DataTable (no extra cell)
  const mainTableColumns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "brand_info",
        header: "Brand",
        accessor: (row: any) => (row.__rowType === "brand" ? row.brand_name : ""),
        render: (_value: any, row: any) => {
          if (row.__rowType === "brand") {
            const isExpanded = row.__isExpanded;
            return (
              <div className="flex items-center gap-2 w-full">
                <Tooltip title={isExpanded ? "Collapse" : "Expand to view accounts"}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExpandRow(row.brand_id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </Tooltip>
                <div className="flex items-center justify-between flex-1 min-w-0">
                  <span className="font-medium truncate">{row.brand_name}</span>
                  <span className="text-sm text-muted-foreground flex-shrink-0 ml-2">{row?.ad_accounts?.length ?? 0} accounts</span>
                </div>
              </div>
            );
          }
          if (row.__rowType === "detail" && row.__parentBrand) {
            const brand = row.__parentBrand as Brand;
            const accounts = brand.ad_accounts ?? [];
            return (
              <div className="py-2 w-full">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <Label className="text-sm font-medium text-muted-foreground">Columns</Label>
                    <MultiSelectCheckboxTag
                      placeholder={selectedColumns.length > 0 ? `Columns (${selectedColumns.length})` : "Select Columns"}
                      options={allColumns.map((col) => ({ value: col, label: col }))}
                      value={selectedColumns}
                      onChange={handleColumnSelectionChange}
                      containerClassName="w-[240px]"
                      showSelectAll
                    />
                  </div>
                  <div className="overflow-auto rounded border bg-card max-h-[420px]">
                    <DataTable
                      className="w-full perf-accounts-nested-datatable"
                      maxHeight="420px"
                      columns={accountsTableColumnsForBrand(brand)}
                      data={accounts}
                      emptyMessage="No ad accounts."
                      stickyHeader
                      showActions={false}
                    />
                  </div>
                </div>
              </div>
            );
          }
          return <span />;
        },
      },
    ],
    [expandedRows, selectedColumns, handleColumnSelectionChange, accountsTableColumnsForBrand]
  );

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Multi-step Add Performance Sheet */}
      <Sheet open={showAddPerfSheet} onOpenChange={(open) => !open && handleAddPerfSheetClose()}>
        <SheetContent className="sm:max-w-xl flex flex-col" onInteractOutside={(e) => e.preventDefault()}>
          <SheetHeader>
            <SheetTitle>Add Performance</SheetTitle>
          </SheetHeader>
          <AddPerformanceForm
            ref={addPerfFormRef}
            calledFromWhichComponent="Media Platform Accounts"
            brands={brands}
            initialBrand={initialBrandForAdd}
            onStepChange={() => {}}
            onClose={() => {
              handleAddPerfSheetClose();
              setInitialBrandForAdd(null);
            }}
            isOpen={showAddPerfSheet}
          />
        </SheetContent>
      </Sheet>
      <EditPerformance calledFromWhichComponent={"Media Platform Accounts"} onSave={getPerfValues} />

      {/* Primary Confirmation Dialog */}
      <Dialog open={aboutToUpdateSourceOfTruthShow} onOpenChange={() => {
        if (!aboutToUpdateSourceOfTruthProgress) {
          setAboutToUpdateSourceOfTruthShow(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Primary Status</DialogTitle>
          </DialogHeader>
                You are about to change the Primary status for account{" "}
            {aboutToUpdateSourceOfTruth?.account_name}. Do you want to continue?
          <DialogFooter>
          <Button
              variant="outline"
            onClick={() => {
              if (!aboutToUpdateSourceOfTruthProgress) {
                  setAboutToUpdateSourceOfTruthShow(false);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!aboutToUpdateSourceOfTruthProgress) {
                updateSourceOfTruthStatus();
              }
            }}
          >
              {aboutToUpdateSourceOfTruthProgress ? (
                <SpinnerLoader className="h-4 w-4" />
              ) : (
                "Yes"
              )}
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* isActive Confirmation Dialog */}
      <Dialog open={aboutToUpdateActivePlatformShow} onOpenChange={() => {
        if (!aboutToUpdateActivePlatformProgress) {
          setAboutToUpdateActivePlatformShow(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Account Active Status</DialogTitle>
          </DialogHeader>
          <div>
                You are about to change the Active status for the account{" "}
                {aboutToUpdateActivePlatform?.account_name}. Do you want to continue?
              </div>
          <DialogFooter>
          <Button
              variant="outline"
            onClick={() => {
              if (!aboutToUpdateActivePlatformProgress) {
                  setAboutToUpdateActivePlatformShow(false);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!aboutToUpdateActivePlatformProgress) {
                updateActivePlatformStatus();
              }
            }}
          >
              {aboutToUpdateActivePlatformProgress ? (
                <SpinnerLoader className="h-4 w-4" />
              ) : (
                "Yes"
              )}
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PageHeader
        title={`Media Platform Accounts ${!searchQuery.trim() ? `(${perfValues.length})` : `(${perfValues.length} of ${perfValuesBackup.length})`}`}
        renderCustomTop={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const user = JSON.parse(localStorage.getItem("user") || "");
                await throttleGetBrands();
                const desc = {
                  message: "Performance accounts list refreshed",
                  category: "performance",
                  action: "refresh_list",
                  user_id: user.id,
                  user_name: user.name,
                };
                await actionLog?.actionLogTrack(JSON.stringify(desc), false);
              }}
              className="flex items-center gap-2 h-8 px-3"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            {hasEditAccess && (
              <Button
                onClick={async () => {
                  const user = JSON.parse(localStorage.getItem("user") || "");
                  setShowAddPerfSheet(true);
                  const desc = {
                    message: "Add Performance wizard opened",
                    category: "performance",
                    action: "add_perf_wizard_opened",
                    user_id: user.id,
                    user_name: user.name,
                  };
                  await actionLog?.actionLogTrack(JSON.stringify(desc), false);
                }}
                size="sm"
                className="flex items-center gap-2 h-8 px-3"
              >
                <Plus className="h-4 w-4" />
                Add Performance
              </Button>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  searchOptimized(event.target.value);
                }}
                className="pl-10 w-64"
              />
            </div>
            {admin_data && (
              <a
                rel="noreferrer"
                href={`https://app.lift.lt.partners/testload?_id=${admin_data.user}&token=${admin_data?.token}&onboard=${1}`}
                target={"_blank"}
              >
                <Button variant="outline" size="sm" className="flex items-center gap-2 h-8 px-3">
                  Go to LIFT
                </Button>
              </a>
            )}
          </div>
        }
      />

      <div className="flex-1 flex flex-col min-h-0 px-8 pt-6 pb-2">
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 160px)"
            columns={mainTableColumns}
            data={flattenedTableData}
            loading={loadList}
            emptyMessage="No brands found."
            stickyHeader
            stickyFooter
            pagination={{
              currentPage: page + 1,
              totalPages: Math.max(1, totalPages),
              pageSize: rowsPerPage,
              totalItems: perfValues.length,
              onPageChange: (p) => handleChangePage(p - 1),
              onPageSizeChange: (size: number) => handleChangeRowsPerPage(String(size)),
              pageSizeOptions: [25, 50, 100, 200],
            }}
            showActions={false}
          />
        </div>
      </div>

    </div>
  );
};

export default PerfAccounts;
